import React, { useState, Children } from 'react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MapPin, Filter, ArrowRight, X, Maximize2 } from 'lucide-react';
import { Link } from 'react-router-dom';
type Frequency = 'daily' | 'weekly' | 'monthly';
interface ServiceEvent {
  id: string;
  title: string;
  time: string;
  location: string;
  image: string;
  category: Frequency;
  link?: string;
}
const servicesData: ServiceEvent[] = [
// DAILY
{
  id: 'morning-cry',
  title: 'Morning Cry Prayer',
  time: '6:00 AM Daily',
  location: 'Online / Phone',
  category: 'daily',
  image: 'https://cdn.magicpatterns.com/uploads/w6RZLmBgZf1M9cyG9K8cYx/MORNING_CRY_-_WEBSITE.jpg'
}, {
  id: 'prayer-line',
  title: 'Prayer Line',
  time: '12:00 PM Daily',
  location: 'Online / Phone',
  category: 'daily',
  image: 'https://cdn.magicpatterns.com/uploads/cFEkVDndjpT59o4n5Zxg9g/PRAYER_LINE_-_WEBSITE.jpg'
},
// WEEKLY
{
  id: 'sunday-worship',
  title: 'Sunday Worship Service',
  time: 'Sundays @ 10:00 AM',
  location: 'TheHousehold Church, Largo MD',
  category: 'weekly',
  image: 'https://cdn.magicpatterns.com/uploads/6QWpts4dzpeFa9omhmVP66/SUNDAY_WORSHIP_-_WEBSITE.jpg'
}, {
  id: 'gap',
  title: 'GAP – God Answers Prayers',
  time: 'Mondays @ 8:00 PM',
  location: 'TheHousehold Church',
  category: 'weekly',
  image: 'https://cdn.magicpatterns.com/uploads/tk4NYadgGLQKz8CcoXdNmK/GAP_-_WEBSITE.png'
}, {
  id: 'impact-wednesday',
  title: 'Impact Wednesday',
  time: 'Wednesdays @ 7:00 PM',
  location: 'TheHousehold Church',
  category: 'weekly',
  image: 'https://cdn.magicpatterns.com/uploads/jKVQXqzASbHqHNRgp1wEeu/IMPACT_WEDNESDAY_-_WEBSITE_FULL.png'
},
// MONTHLY
{
  id: 'communion',
  title: 'Communion Service',
  time: 'Every 1st Sunday of the month',
  location: 'TheHousehold Church',
  category: 'monthly',
  image: 'https://cdn.magicpatterns.com/uploads/epCJJMvmRPcNahjc1gvPxT/HOLY_COMMUNION_-_WEBSITE.png'
}, {
  id: 'let-god-arise',
  title: 'Let God Arise',
  time: '1st Friday and Saturday every month',
  location: 'TheHousehold Church',
  category: 'monthly',
  image: 'https://cdn.magicpatterns.com/uploads/6RDrwDLyFxxLoNdtau8fLt/LET_GOD_ARISE_-_WEBSITE.png'
}];
const filters = [{
  id: 'all',
  label: 'All Services'
}, {
  id: 'daily',
  label: 'Daily'
}, {
  id: 'weekly',
  label: 'Weekly'
}, {
  id: 'monthly',
  label: 'Monthly'
}];
export function EventsPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState<ServiceEvent | null>(null);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  const renderServiceCard = (service: ServiceEvent) => {
    const handleClick = () => {
      if (!service.link) {
        setSelectedEvent(service);
      }
    };
    const CardContent = <motion.div layout initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl hover:border-[#fc0719]/30 transition-all duration-300 h-full flex flex-col group cursor-pointer" onClick={handleClick} data-id="element-665">
        <div className="aspect-video w-full overflow-hidden relative bg-[#0A1F44]" data-id="element-666">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-667" />
          {/* Expand icon overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center" data-id="element-668">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg" data-id="element-669">
              <Maximize2 className="w-5 h-5 text-[#0A1F44]" data-id="element-670" />
            </div>
          </div>
        </div>
        <div className="p-6 flex flex-col flex-grow" data-id="element-671">
          <h3 className="text-xl font-bold text-[#0A1F44] mb-4 group-hover:text-[#fc0719] transition-colors leading-tight font-serif" data-id="element-672">
            {service.title}
          </h3>
          <div className="space-y-3 mb-6" data-id="element-673">
            <div className="flex items-start gap-3 text-gray-600" data-id="element-674">
              <Clock className="w-5 h-5 text-[#fc0719] shrink-0 mt-0.5" data-id="element-675" />
              <span className="text-sm font-medium" data-id="element-676">{service.time}</span>
            </div>
            <div className="flex items-start gap-3 text-gray-600" data-id="element-677">
              <MapPin className="w-5 h-5 text-[#fc0719] shrink-0 mt-0.5" data-id="element-678" />
              <span className="text-sm font-medium" data-id="element-679">{service.location}</span>
            </div>
          </div>

          <div className="mt-auto pt-4 border-t border-gray-100 flex items-center text-[#fc0719] font-bold text-xs uppercase tracking-wider group-hover:translate-x-2 transition-transform" data-id="element-680">
            View Details
            <ArrowRight className="w-4 h-4 ml-2" data-id="element-681" />
          </div>
        </div>
      </motion.div>;
    return <motion.div key={service.id} variants={itemVariants} className="h-full" data-id="element-682">
        {service.link ? <Link to={service.link} className="block h-full" data-id="element-683">
            {CardContent}
          </Link> : <div onClick={handleClick} className="h-full" data-id="element-684">
            {CardContent}
          </div>}
      </motion.div>;
  };
  const renderSection = (title: string, category: Frequency) => {
    const sectionEvents = servicesData.filter(s => s.category === category);
    if (sectionEvents.length === 0) return null;
    return <div className="mb-16 last:mb-0" data-id="element-685">
        <div className="flex items-center gap-4 mb-8" data-id="element-686">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#0A1F44] uppercase tracking-wide" data-id="element-687">
            {title}
          </h2>
          <div className="h-[1px] flex-grow bg-gray-200" data-id="element-688" />
        </div>
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-689">
          {sectionEvents.map(renderServiceCard)}
        </motion.div>
      </div>;
  };
  return <Layout data-id="element-690">
      {/* Flyer Detail Modal */}
      <AnimatePresence data-id="element-691">
        {selectedEvent && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedEvent(null)} data-id="element-692">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="bg-white rounded-2xl overflow-hidden max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-693">
              {/* Large flyer image with close button overlay */}
              <div className="w-full relative" data-id="element-694">
                <button onClick={() => setSelectedEvent(null)} className="absolute top-3 right-3 z-10 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors" data-id="element-695">
                  <X className="w-5 h-5 text-white" data-id="element-696" />
                </button>
                <img src={selectedEvent.image} alt={selectedEvent.title} className="w-full h-auto object-cover" data-id="element-697" />
              </div>

              {/* Event details */}
              <div className="p-8 md:p-10" data-id="element-698">
                <div className="flex items-center gap-2 text-[#fc0719] font-bold text-xs uppercase tracking-[0.2em] mb-4" data-id="element-699">
                  <div className="h-[1px] w-8 bg-[#fc0719]" data-id="element-700" />
                  Service Details
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0A1F44] mb-8 leading-tight" data-id="element-701">
                  {selectedEvent.title}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10" data-id="element-702">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100" data-id="element-703">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center shrink-0" data-id="element-704">
                      <Clock className="w-6 h-6 text-[#fc0719]" data-id="element-705" />
                    </div>
                    <div data-id="element-706">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1" data-id="element-707">
                        When
                      </p>
                      <p className="font-bold text-[#0A1F44]" data-id="element-708">
                        {selectedEvent.time}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100" data-id="element-709">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center shrink-0" data-id="element-710">
                      <MapPin className="w-6 h-6 text-[#fc0719]" data-id="element-711" />
                    </div>
                    <div data-id="element-712">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1" data-id="element-713">
                        Where
                      </p>
                      <p className="font-bold text-[#0A1F44]" data-id="element-714">
                        {selectedEvent.location}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4" data-id="element-715">
                  <Button onClick={() => setSelectedEvent(null)} className="flex-1 bg-[#0A1F44] hover:bg-[#081833] text-white font-bold py-4 tracking-widest uppercase text-xs" data-id="element-716">
                    Close
                  </Button>
                  {selectedEvent.link && <Link to={selectedEvent.link} className="flex-1" data-id="element-717">
                      <Button variant="primary" className="w-full font-bold py-4 tracking-widest uppercase text-xs" data-id="element-718">
                        Learn More
                      </Button>
                    </Link>}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#0A1F44] overflow-hidden" data-id="element-719">
        <div className="absolute inset-0 opacity-30" data-id="element-720">
          <img src="https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" alt="Events background" className="w-full h-full object-cover" data-id="element-721" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44]/90 via-[#0A1F44]/80 to-[#0A1F44]" data-id="element-722" />

        <div className="container-custom relative z-10" data-id="element-723">
          <div className="max-w-4xl mx-auto text-center py-12 md:py-20" data-id="element-724">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-725">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl mb-8 border border-white/20" data-id="element-726">
                <Calendar className="w-10 h-10 text-[#fc0719]" data-id="element-727" />
              </div>
              <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-4 tracking-tight" data-id="element-728">
                All Services & <span className="text-[#fc0719]" data-id="element-729">Events</span>
              </h1>
              <p className="text-[#fc0719] font-bold text-sm uppercase tracking-[0.3em] mb-8" data-id="element-730">
                Never Miss a Moment
              </p>
              <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed font-medium" data-id="element-731">
                Join us for worship, fellowship, and community. Discover our
                daily, weekly, and monthly gatherings as we grow together in
                faith.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#fc0719]/10 rounded-full blur-[120px] -mr-48 -mt-48" data-id="element-732" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] -ml-48 -mb-48" data-id="element-733" />
      </section>

      {/* Filters Section */}
      <section className="sticky py-8 bg-white border-b top-[72px] md:top-[109px] z-30 shadow-sm" data-id="element-734">
        <div className="container-custom" data-id="element-735">
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6" data-id="element-736">
            <div className="flex items-center gap-2 text-gray-400 font-bold text-xs uppercase tracking-[0.2em] mr-2 hidden lg:flex" data-id="element-737">
              <Filter className="w-4 h-4" data-id="element-738" />
              Filter By:
            </div>
            {filters.map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter.id)} className={`px-6 py-2.5 rounded-full text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300 border-2 ${activeFilter === filter.id ? 'bg-[#fc0719] text-white border-[#fc0719] shadow-lg shadow-red-600/20' : 'bg-transparent text-gray-500 border-gray-100 hover:border-gray-300 hover:text-gray-700'}`} data-id="element-739">
                {filter.label}
              </button>)}
          </div>
        </div>
      </section>

      {/* Events Sections */}
      <section className="py-16 bg-[#F5F7FA] min-h-[500px]" data-id="element-740">
        <div className="container-custom" data-id="element-741">
          {(activeFilter === 'all' || activeFilter === 'daily') && renderSection('Daily Services', 'daily')}
          {(activeFilter === 'all' || activeFilter === 'weekly') && renderSection('Weekly Services', 'weekly')}
          {(activeFilter === 'all' || activeFilter === 'monthly') && renderSection('Monthly Services', 'monthly')}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white" data-id="element-742">
        <div className="container-custom" data-id="element-743">
          <div className="bg-gradient-to-r from-[#0A1F44] to-[#163A70] rounded-2xl p-10 md:p-16 text-center relative overflow-hidden shadow-2xl" data-id="element-744">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#fc0719]/20 rounded-full blur-[80px]" data-id="element-745" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#B8860B]/20 rounded-full blur-[80px]" data-id="element-746" />

            <div className="relative z-10" data-id="element-747">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6" data-id="element-748">
                Want to Host an Event?
              </h2>
              <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-749">
                Have an idea for a community event or ministry gathering? We'd
                love to hear from you and help make it happen.
              </p>
              <Link to="/contact" data-id="element-750">
                <Button className="bg-[#fc0719] hover:bg-[#d4061a] text-white border-none font-bold tracking-widest uppercase px-10 py-4 shadow-[0_0_20px_rgba(252,7,25,0.4)]" size="lg" data-id="element-751">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}